import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RightSheet from "../../../components/ui-components/RightSheet.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../../components/ui-components/buttonStyles";
import { usePreviewMutation, useCreateInvestigationMutation } from "../hooks";
import { fmtSignedMoney } from "../format";
import DriverDecompositionTable from "./DriverDecompositionTable";

const QUESTION_TYPES = [
  { value: "total_spend", label: "Why did total spend change?" },
  { value: "total_usage", label: "Why did total usage change?" },
  { value: "biggest_mover", label: "What's the biggest mover?" },
  { value: "budget_breach", label: "Why did a budget breach?" },
  { value: "forecast", label: "Why did the forecast change?" },
  { value: "manual", label: "Manual / other" },
];

// Last full calendar month, computed with plain JS Date (from = 1st of last month,
// to = last day of last month).
function lastFullMonthRange() {
  const now = new Date();
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 = last day of previous month
  return { from: toISO(from), to: toISO(to) };
}

function buildSeed({ questionType, period }) {
  const metric = questionType === "total_usage" ? "usage" : "cost";
  // Only total_spend / total_usage / biggest_mover are wired to a concrete scope in v1;
  // biggest_mover leaves the specific mover for the backend to pick. budget_breach /
  // forecast / manual keep the same "total" scope for now — the guided form stays
  // minimal and doesn't yet collect a dimension/ref.
  return { questionType, metric, scope: { kind: "total" }, period, baseline: "prior_window" };
}

function defaultTitle(questionType, period) {
  const label = QUESTION_TYPES.find((q) => q.value === questionType)?.label || "Investigation";
  return `${label.replace(/\?$/, "")} — ${period.from} to ${period.to}`;
}

/**
 * NewInvestigationModal — guided create flow: pick a question type + period,
 * Preview the decomposition (non-persisting), then Create to persist and navigate
 * to the new investigation's detail page.
 */
export default function NewInvestigationModal({ open, onClose, initialSeed }) {
  const navigate = useNavigate();
  const preview = usePreviewMutation();
  const create = useCreateInvestigationMutation();

  const [questionType, setQuestionType] = useState(initialSeed?.questionType || "total_spend");
  const [period, setPeriod] = useState(initialSeed?.period || lastFullMonthRange());
  const [title, setTitle] = useState("");
  const [decomposition, setDecomposition] = useState(null);

  // Re-seed the form whenever the sheet is (re)opened with a fresh initialSeed
  // (e.g. from InvestigateButton), and auto-run the preview so the user lands
  // straight on the decomposition instead of an empty form.
  useEffect(() => {
    if (!open) return;
    setQuestionType(initialSeed?.questionType || "total_spend");
    setPeriod(initialSeed?.period || lastFullMonthRange());
    setTitle("");
    setDecomposition(null);
    if (initialSeed) {
      preview.mutateAsync(buildSeed({ questionType: initialSeed.questionType, period: initialSeed.period })).then((res) => {
        setDecomposition(res?.decomposition ?? null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSeed]);

  const handlePreview = async () => {
    const seed = buildSeed({ questionType, period });
    const res = await preview.mutateAsync(seed);
    setDecomposition(res?.decomposition ?? null);
  };

  const handleCreate = async () => {
    const seed = buildSeed({ questionType, period });
    const finalTitle = title.trim() || defaultTitle(questionType, period);
    const res = await create.mutateAsync({ title: finalTitle, seed });
    const id = res?.investigation?.id;
    onClose();
    if (id) navigate(`/investigations/${id}`);
  };

  const topDrivers = (decomposition?.drivers || []).slice(0, 3);

  return (
    <RightSheet
      open={open}
      onClose={onClose}
      title="New investigation"
      subtitle="Pick what you want explained — preview it, then save."
      footer={
        <div className="flex items-center justify-end gap-3">
          <button type="button" className={secondaryButtonCls} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={secondaryButtonCls} onClick={handlePreview} disabled={preview.isLoading}>
            {preview.isLoading ? "Previewing…" : "Preview"}
          </button>
          <button type="button" className={primaryButtonCls} onClick={handleCreate} disabled={create.isLoading}>
            {create.isLoading ? "Creating…" : "Create"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="investigation-title" className="text-xs font-medium text-text-2">
            Title
          </label>
          <input
            id="investigation-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={defaultTitle(questionType, period)}
            className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label htmlFor="investigation-question-type" className="text-xs font-medium text-text-2">
            Question
          </label>
          <select
            id="investigation-question-type"
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value)}
            className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
          >
            {QUESTION_TYPES.map((q) => (
              <option key={q.value} value={q.value}>
                {q.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label htmlFor="investigation-from" className="text-xs font-medium text-text-2">
              From
            </label>
            <input
              id="investigation-from"
              type="date"
              value={period.from}
              onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="investigation-to" className="text-xs font-medium text-text-2">
              To
            </label>
            <input
              id="investigation-to"
              type="date"
              value={period.to}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
              className="mt-1 w-full rounded-ctrl border border-border px-3 py-2 text-sm"
            />
          </div>
        </div>

        {decomposition && (
          <div className="mt-2 rounded-card border border-card-bd bg-card p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-text-2">Preview</div>
            <div className="mt-1 text-xl font-semibold tabular-nums text-text">
              {fmtSignedMoney(decomposition.deltaCost)}
            </div>
            <div className="mt-3">
              <DriverDecompositionTable drivers={topDrivers} />
            </div>
          </div>
        )}
      </div>
    </RightSheet>
  );
}
